import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isLoggedIn, clearToken, fetchTrainings,
  createTraining, updateTraining, deleteTraining, imageUrl,
} from '../../api/client';
import AdminNav from './AdminNav';
import './Admin.css';

const emptyForm = {
  title: '', type: 'Training', description: '', duration: '', mode: 'Offline', sort_order: 0,
};

function IconEdit() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M14.2 2.8a1.8 1.8 0 012.5 2.5L6 15.9l-3.4.9.9-3.4L14.2 2.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M4 6h12M8 6V4.5a1 1 0 011-1h2a1 1 0 011 1V6m-7 0l.7 9.2a1.5 1.5 0 001.5 1.4h4.6a1.5 1.5 0 001.5-1.4L15 6"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconImage() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8.5" cy="9.5" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M21 15l-5-5-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null); // { type, text }

  useEffect(() => {
    if (!isLoggedIn()) { navigate('/admin', { replace: true }); return; }
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = () => {
    setLoading(true);
    fetchTrainings()
      .then(setItems)
      .catch(() => setToast({ type: 'err', text: 'Could not load trainings.' }))
      .finally(() => setLoading(false));
  };

  const handleLogout = () => { clearToken(); navigate('/admin'); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    setImageFile(file || null);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setImageFile(null);
    setPreview(null);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title, type: item.type, description: item.description,
      duration: item.duration, mode: item.mode, sort_order: item.sort_order,
    });
    setImageFile(null);
    setPreview(item.image_url ? imageUrl(item.image_url) : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setToast({ type: 'err', text: 'Title is required.' }); return; }

    setSaving(true);
    setToast(null);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (imageFile) fd.append('image', imageFile);

    try {
      if (editingId) {
        await updateTraining(editingId, fd);
        setToast({ type: 'ok', text: 'Training updated.' });
      } else {
        await createTraining(fd);
        setToast({ type: 'ok', text: 'Training added.' });
      }
      resetForm();
      loadItems();
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training? This cannot be undone.')) return;
    try {
      await deleteTraining(id);
      setToast({ type: 'ok', text: 'Deleted.' });
      loadItems();
      if (editingId === id) resetForm();
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Delete failed.' });
    }
  };

  return (
    <div className="adm">
      <div className="adm-topbar">
        <h1 className="adm-topbar__title">Training &amp; Workshops — Admin</h1>
        <button className="adm-logout" onClick={handleLogout}>Sign Out</button>
      </div>

      <AdminNav active="trainings" />

      <div className="adm-body">

        {/* ── Form ── */}
        <div className="adm-card">
          <h2 className="adm-card__title">{editingId ? 'Edit Training' : 'Add New Training'}</h2>

          {toast && (
            <div className={`adm-toast adm-toast--${toast.type}`}>{toast.text}</div>
          )}

          <form className="adm-form-grid" onSubmit={handleSubmit}>
            <div className="adm-field">
              <label htmlFor="title">Title *</label>
              <input id="title" name="title" value={form.title} onChange={handleChange}
                     placeholder="e.g. Full Stack Web Development" />
            </div>

            <div className="adm-row-2">
              <div className="adm-field">
                <label htmlFor="type">Type</label>
                <select id="type" name="type" value={form.type} onChange={handleChange}>
                  <option value="Training">Training</option>
                  <option value="Workshop">Workshop</option>
                </select>
              </div>
              <div className="adm-field">
                <label htmlFor="mode">Mode</label>
                <select id="mode" name="mode" value={form.mode} onChange={handleChange}>
                  <option value="Offline">Offline</option>
                  <option value="Online">Online</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            <div className="adm-field">
              <label htmlFor="duration">Duration</label>
              <input id="duration" name="duration" value={form.duration} onChange={handleChange}
                     placeholder="e.g. 6 Weeks / 2-Day Workshop" />
            </div>

            <div className="adm-field">
              <label htmlFor="description">Description</label>
              <textarea id="description" name="description" value={form.description} onChange={handleChange}
                        placeholder="Short summary shown on the Training page" />
            </div>

            <div className="adm-field">
              <label htmlFor="sort_order">Display Order (lower shows first)</label>
              <input id="sort_order" name="sort_order" type="number" value={form.sort_order} onChange={handleChange} />
            </div>

            <div className="adm-field">
              <label htmlFor="image">Image</label>
              <input id="image" type="file" accept="image/*" className="adm-file-input" onChange={handleFile} />
              {preview && <img src={preview} alt="Preview" className="adm-img-preview" />}
            </div>

            <div className="adm-form-actions">
              <button className="adm-btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update Training' : 'Add Training'}
              </button>
              {editingId && (
                <button type="button" className="adm-btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── List ── */}
        <div className="adm-card">
          <h2 className="adm-card__title">All Trainings ({items.length})</h2>

          {loading && <p className="adm-empty">Loading…</p>}
          {!loading && items.length === 0 && <p className="adm-empty">No trainings added yet.</p>}

          {!loading && items.length > 0 && (
            <div className="adm-list">
              {items.map((item) => (
                <div className="adm-item" key={item.id}>
                  {item.image_url ? (
                    <img className="adm-item__thumb" src={imageUrl(item.image_url)} alt={item.title} />
                  ) : (
                    <div className="adm-item__thumb-fallback"><IconImage /></div>
                  )}
                  <div className="adm-item__body">
                    <p className="adm-item__title">{item.title}</p>
                    <p className="adm-item__meta">{item.type} · {item.mode}{item.duration ? ` · ${item.duration}` : ''}</p>
                  </div>
                  <div className="adm-item__actions">
                    <button className="adm-icon-btn" onClick={() => startEdit(item)} aria-label="Edit"><IconEdit /></button>
                    <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => handleDelete(item.id)} aria-label="Delete"><IconTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}